import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { cn } from '@/lib/utils';

export interface ValidationError {
  field: string;
  label: string;
  message: string;
}

interface ValidationSummaryPanelProps {
  errors: ValidationError[];
  isVisible: boolean;
  onDismiss?: () => void;
  onFieldClick?: (field: string) => void;
  className?: string;
}

export const ValidationSummaryPanel: React.FC<ValidationSummaryPanelProps> = ({
  errors,
  isVisible,
  onDismiss,
  onFieldClick,
  className,
}) => {
  const { triggerHaptic } = useHapticFeedback();

  // Trigger haptic when panel appears with errors
  React.useEffect(() => {
    if (isVisible && errors.length > 0) {
      triggerHaptic('warning');
    }
  }, [isVisible, errors.length, triggerHaptic]);

  const handleFieldClick = (field: string) => {
    triggerHaptic('light');
    onFieldClick?.(field);
  };

  return (
    <AnimatePresence>
      {isVisible && errors.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            "relative overflow-hidden rounded-lg border border-destructive/50 bg-destructive/10 backdrop-blur-sm",
            className
          )}
        >
          {/* Gradient background accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-destructive/5 to-destructive/10 pointer-events-none" />
          
          {/* Header */}
          <div className="relative flex items-center justify-between px-4 py-3 border-b border-destructive/20">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span className="font-medium text-destructive">
                Please fix {errors.length} error{errors.length > 1 ? 's' : ''} before calculating
              </span>
            </div>
            {onDismiss && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                onClick={onDismiss}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Error List */}
          <ul className="relative divide-y divide-destructive/10">
            {errors.map((error, index) => (
              <motion.li
                key={`${error.field}-${index}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "flex items-center justify-between px-4 py-2.5 text-sm",
                  onFieldClick && "cursor-pointer hover:bg-destructive/5 transition-colors"
                )}
                onClick={() => onFieldClick && handleFieldClick(error.field)}
              >
                <div className="flex-1">
                  <span className="font-medium text-foreground">{error.label}:</span>
                  <span className="ml-2 text-muted-foreground">{error.message}</span>
                </div>
                {onFieldClick && (
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
              </motion.li>
            ))}
          </ul>

          {/* Footer with action */}
          {errors.length > 0 && onFieldClick && (
            <div className="relative px-4 py-2 border-t border-destructive/20 bg-destructive/5">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full"
                onClick={() => handleFieldClick(errors[0].field)}
              >
                Go to first error
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ValidationSummaryPanel;
